<template>
    <section class="SortBar">
        <section
            :class="[
                'SortBarItems',
                { 'withUniversityRankingToggle': showUniversityRankingToggle }
            ]"
        >
            <CurrencySelector
                v-if="searchStore.searchType === SearchType.PROGRAMME_SEARCH"
                :currency="userCurrency"
                @click="triggerCurrencyChange"
            />
            <SortSelector
                v-if="searchStore.searchType === SearchType.PROGRAMME_SEARCH"
                @click="handleShow"
                @click-outside="handleClickOutside"
            />
            <RankingSorting
                v-if="showUniversityRankingToggle"
            />
            <FilterBtn ref="filterBtnObserverTarget"/>
        </section>
        <SortPanel v-if="!isMobile && showSortPanel" :show="true"/>
    </section>

    <Transition name="FloatingFilterTransition">
        <section
            v-if="showFloatingFilterBtn"
            class="FloatingFilterContainer"
        >
            <FilterBtn/>
        </section>
    </Transition>
</template>

<script lang="ts" setup>
import CurrencySelector from './CurrencySelector/CurrencySelector.vue';
import SortSelector from './SortSelector/SortSelector.vue';
import FilterBtn from './FilterBtn/FilterBtn.vue';
import SortPanel from './SortPanel/SortPanel.vue';
import RankingSorting from './RankingSorting/RankingSorting.vue';
import { useCurrency } from '@composables/useCurrency';
import { useLocalizedContent } from '@app/composables/useLocalizedContent';
import {useMobile} from '@composables/useMobile';
import {useMobileModal} from '@composables/useMobileModal';
import { ComponentPublicInstance, computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useSearchStore } from '@app/stores/useSearchStore';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { SortMapper, UNIVERSITY_RANKINGS_SORT } from '@app/routing/mappers/SortMapper';
import { SearchType } from '@domain/enums/SearchType';
import { getPortalTypeFromEnvClient } from "@utils/get-portal-type-from-env.client.ts";

const { isMobile } = useMobile();
const { open: openModal, close: closeModal } = useMobileModal();
const {userCurrency, triggerCurrencyChange} = useCurrency();
const searchStore = useSearchStore();
const showSortPanel = ref(false);
const filterBtnObserverTarget = ref<ComponentPublicInstance | null>(null);
const isFilterBtnVisible = ref(true);
const isPaginationVisible = ref(false);
const paginationElementSelector = '.Pagination';
const paginationElement = ref<Element | null>(null);
const filterBtnElement = ref<Element | null>(null);
let intersectionObserver: IntersectionObserver | null = null;
let mutationObserver: MutationObserver | null = null;

const showFloatingFilterBtn = computed<boolean>(() =>
    isMobile.value && !isFilterBtnVisible.value && !isPaginationVisible.value
);

const showUniversityRankingToggle = computed<boolean>(() =>
    searchStore.searchType === SearchType.UNIVERSITY_SEARCH && getPortalTypeFromEnvClient() !== 'master'
);

const isUniversityRankingEnabled = computed<boolean>(() => {
    const sortingFilter = searchStore.filtersSelection.find(filter => filter.type === FilterType.SORTING);
    if (!sortingFilter) return false;

    return sortingFilter.values.some(value => value.id === UNIVERSITY_RANKINGS_SORT);
});

const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
    for (const entry of entries) {
        if (entry.target === filterBtnElement.value) {
            isFilterBtnVisible.value = entry.isIntersecting;
        }

        if (entry.target === paginationElement.value) {
            isPaginationVisible.value = entry.isIntersecting;
        }
    }
};

const connectFilterBtnObserver = (): void => {
    const target = filterBtnObserverTarget.value?.$el as Element | undefined;
    if (!target || !intersectionObserver || filterBtnElement.value === target) return;

    if (filterBtnElement.value) {
        intersectionObserver.unobserve(filterBtnElement.value);
    }

    filterBtnElement.value = target;
    intersectionObserver.observe(target);
};

const connectPaginationObserver = (): void => {
    const pagination = document.querySelector(paginationElementSelector);
    if (!pagination || !intersectionObserver || paginationElement.value === pagination) return;

    if (paginationElement.value) {
        intersectionObserver.unobserve(paginationElement.value);
    }

    paginationElement.value = pagination;
    intersectionObserver.observe(pagination);
};

const setupObservers = async (): Promise<void> => {
    if (typeof window === 'undefined' || intersectionObserver) return;
    await nextTick();

    intersectionObserver = new IntersectionObserver(handleIntersection, {
        root: null,
        threshold: 0.1,
    });

    connectFilterBtnObserver();
    connectPaginationObserver();

    mutationObserver = new MutationObserver(() => {
        connectFilterBtnObserver();
        connectPaginationObserver();
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
};

const teardownObservers = (): void => {
    if (intersectionObserver) {
        intersectionObserver.disconnect();
        intersectionObserver = null;
    }

    if (mutationObserver) {
        mutationObserver.disconnect();
        mutationObserver = null;
    }

    paginationElement.value = null;
    filterBtnElement.value = null;
};

const handleShow = () => {
    if (isMobile.value) {
        showSortPanel.value = false;
        openModal(SortPanel, { show: true });
    } else {
        showSortPanel.value = !showSortPanel.value;
    }
};

const handleClickOutside = () => {
    if (showSortPanel.value) {
        showSortPanel.value = false;
    }
};

watch(isMobile, (mobile) => {
    if (mobile) {
        showSortPanel.value = false;
        void setupObservers();
        return;
    }

    teardownObservers();
    isFilterBtnVisible.value = true;
    isPaginationVisible.value = false;
    closeModal();
});

onMounted(() => {
    if (isMobile.value) {
        void setupObservers();
    }
});

onBeforeUnmount(() => {
    teardownObservers();
});

useLocalizedContent();
</script>

<style scoped lang="scss">
.SortBar {
    display: flex;
    flex-direction: column;
    width: 100%;

    .SortBarItems {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.5rem;
        @include PaddingAreas(0.5, 0);

        @include Breakpoint(Medium, Large, ExtraLarge) {
            :not(.withUniversityRankingToggle) {
                justify-content: flex-end;
            }
        }
    }
}

.UniversityRankingToggle {
    border: 1px solid $Grey-300;
    border-radius: 0.25rem;
    background: $White;
    color: $Grey-900;
    cursor: pointer;
    @include PaddingAreas(0.5);
    @include TextStyle(UiSmallSemibold);
}

.FloatingFilterContainer {
    position: fixed;
    left: 50%;
    bottom: 1rem;
    transform: translateX(-50%);
    z-index: $SecondFloor;
    background: $White;
    border: 1px solid $Grey-300;
    border-radius: 0.25rem;

    @include Shadow(Low);
    @include PaddingAreas(0.5);

    @include Breakpoint(Large, ExtraLarge) {
        display: none;
    }
}

.FloatingFilterTransition-enter-active,
.FloatingFilterTransition-leave-active {
    transition: opacity 0.22s ease, transform 0.22s ease;
}

.FloatingFilterTransition-enter-from,
.FloatingFilterTransition-leave-to {
    transform: translate(-50%, 4rem);
}

.FloatingFilterTransition-enter-to,
.FloatingFilterTransition-leave-from {
    transform: translate(-50%, 0);
}
</style>
